import { RecapPanel } from './components/RecapPanel';
import { TranscriptLayout } from './components/TranscriptLayout';
import { TranscriptReadingPanel } from './components/TranscriptReadingPanel';
import { TranscriptUploadPanel } from './components/TranscriptUploadPanel';

import type { MeetingTranscriptProps } from './types';

/** What the evaluation journey has always accepted; a caller taking more says so explicitly. */
const DEFAULT_ACCEPTED_FILE_TYPES = '.txt,.md';

/**
 * Presentation of the "import a meeting transcript, read the AI recap" screen. It holds no data of its own:
 * fetching, uploading, generating and downloading belong to the flow that renders it, so the same screen
 * serves the evaluation journey and the thematic meetings without either one owning the other's wording.
 */
export const MeetingTranscript = ({
    spacing = 'standalone',
    dataTestId,
    ...panelProps
}: MeetingTranscriptProps) => {
    return (
        <TranscriptLayout
            dataTestId={dataTestId}
            labels={panelProps.labels}
            spacing={spacing}
        >
            <TranscriptPanel {...panelProps} />
        </TranscriptLayout>
    );
};

type TranscriptPanelProps = Omit<MeetingTranscriptProps, 'spacing' | 'dataTestId'>;

/**
 * The transcript being read comes first, whether or not one exists: until it is known, both the
 * invitation to import one and the recap panel would be guesses.
 */
const TranscriptPanel = ({
    labels,
    isLoading = false,
    transcriptLoadFailure,
    hasTranscript,
    recap,
    isBusy,
    fileInputRef,
    onChooseFile,
    onFileSelected,
    recapRetry,
    onDownloadTranscript,
    onDownloadRecap,
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    disabled = false,
}: TranscriptPanelProps) => {
    if (isLoading || transcriptLoadFailure) {
        return (
            <TranscriptReadingPanel
                failure={transcriptLoadFailure}
                disabled={disabled}
            />
        );
    }

    if (!hasTranscript) {
        return (
            <TranscriptUploadPanel
                labels={labels}
                isUploading={isBusy}
                fileInputRef={fileInputRef}
                onChooseFile={onChooseFile}
                onFileSelected={onFileSelected}
                acceptedFileTypes={acceptedFileTypes}
                disabled={disabled}
            />
        );
    }

    return (
        <RecapPanel
            labels={labels}
            recap={recap}
            isBusy={isBusy}
            fileInputRef={fileInputRef}
            onImportAnother={onChooseFile}
            onFileSelected={onFileSelected}
            recapRetry={recapRetry}
            onDownloadTranscript={onDownloadTranscript}
            onDownloadRecap={onDownloadRecap}
            acceptedFileTypes={acceptedFileTypes}
            disabled={disabled}
        />
    );
};
